import React, { useEffect, useState } from "react";
import { Megaphone, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useEnquirySources } from "@/components/EnquirySource";
import { BrandIcon } from "@/components/BrandIcon";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");

function SourceRow({ s, onChanged }) {
  const [name, setName] = useState(s.name);
  const patch = (body) => api.patch(`/enquiry-sources/${s.id}`, body).then(onChanged).catch(err);
  return (
    <div className="flex items-center gap-2" data-testid={`enquiry-source-row-${s.slug}`}>
      <span className="w-9 h-9 shrink-0 rounded-lg border border-slate-200 bg-white flex items-center justify-center"><BrandIcon name={s.name} size={18} testid={`enquiry-source-icon-${s.slug}`} /></span>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" data-testid={`enquiry-source-name-${s.slug}`} />
      <Button variant="outline" size="sm" disabled={name.trim() === s.name || !name.trim()} onClick={() => patch({ name })} data-testid={`enquiry-source-save-${s.slug}`}>Save</Button>
      <Switch checked={s.active} onCheckedChange={(v) => patch({ active: v })} data-testid={`enquiry-source-active-${s.slug}`} />
    </div>
  );
}

function ZapierKey() {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const load = () => api.get("/integrations/zapier/facebook-swim-spa").then((r) => setSt(r.data)).catch(() => setSt(null));
  useEffect(() => { load(); }, []);
  const rotate = async () => {
    if (st?.configured && !window.confirm("Generate a new key? The current Zapier key will stop working immediately.")) return;
    try { const { data } = await api.post("/integrations/zapier/facebook-swim-spa/key"); setKey(data.api_key); load(); } catch (e) { err(e); }
  };
  if (!st) return null;
  const url = `${process.env.REACT_APP_BACKEND_URL}${st.endpoint_path}`;
  return (
    <div className="mt-5 pt-4 border-t border-slate-100 space-y-2" data-testid="zapier-fb-card">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><KeyRound className="w-4 h-4" /> Inbound enquiry API (Zapier)</h3>
      <p className="text-xs text-slate-500 break-all">POST <span className="font-mono" data-testid="zapier-fb-url">{url}</span></p>
      <p className="text-xs text-slate-500 break-all">Other sources: POST <span className="font-mono" data-testid="zapier-generic-url">{process.env.REACT_APP_BACKEND_URL}/api/inbound/enquiries</span> with a <span className="font-mono">source</span> field</p>
      <p className="text-xs text-slate-500" data-testid="zapier-fb-status">
        {st.configured ? `Key ${st.key_prefix}… active${st.last_used_at ? ` · last used ${new Date(st.last_used_at).toLocaleString()}` : ""}` : "No key yet"}
      </p>
      {key && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2" data-testid="zapier-fb-new-key">
          <span className="font-mono text-xs break-all flex-1">{key}</span>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(key); toast.success("Copied"); }} data-testid="zapier-fb-copy-key"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
      )}
      {key && <p className="text-xs text-amber-700">Copy it now — it won't be shown again. Use it as the header X-TedOps-Key.</p>}
      <Button size="sm" onClick={rotate} data-testid="zapier-fb-generate-key">{st.configured ? "Generate new key" : "Generate key"}</Button>
    </div>
  );
}

export default function EnquirySourcesCard() {
  const [sources, reload] = useEnquirySources(true);
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post("/enquiry-sources", { name }); setName(""); reload(); toast.success("Source added"); } catch (e) { err(e); }
  };
  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-3" data-testid="enquiry-sources-card">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Megaphone className="w-4 h-4" /> Enquiry sources</h2>
      <p className="text-xs text-slate-500">Where enquiries come from. Disabled sources stay on existing jobs but can't be picked for new ones.</p>
      <div className="space-y-2">{sources.map((s) => <SourceRow key={`${s.id}-${s.name}-${s.active}`} s={s} onChanged={reload} />)}</div>
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New source name" className="h-9" data-testid="enquiry-source-new-input" />
        <Button size="sm" disabled={!name.trim()} onClick={add} data-testid="enquiry-source-add-btn">Add</Button>
      </div>
      <ZapierKey />
    </section>
  );
}
